import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  SuccessModalComponent,
  SuccessModalData,
} from '../../shared/components/success-modal/success-modal.component';
import {
  buildPaymentSuccessModalData,
  PAYMENT_SUCCESS_MODAL_DEFAULTS,
} from '../../shared/utils/payment-success-modal.util';
import { OrderDetailsModalComponent } from '../order-details-modal/order-details-modal.component';
import { DiscardChangesModalComponent } from '../../shared/components/discard-changes-modal/discard-changes-modal.component';
import { OrderStepperComponent, StepTab } from './components/order-stepper/order-stepper.component';
import {
  PackageStepComponent,
  PackageSelection,
} from './components/package-step/package-step.component';
import {
  CustomerInfoStepComponent,
  CustomerResolvedEvent,
  LocationOption,
} from './components/customer-info-step/customer-info-step.component';
import {
  VehicleStepComponent,
  VehicleStepResult,
} from './components/vehicle-step/vehicle-step.component';
import {
  PaymentStepComponent,
  PriceBreakdown,
  PaymentSummary,
} from './components/payment-step/payment-step.component';
import { AddOrderStateService, PaymentMethod } from './services/add-order-state.service';
import { PaymentMethodCode } from '../../shared/types/payment-method.enum';
import { TapToPayService } from './services/tap-to-pay.service';

import {
  OrderCreationService,
  CreateOrderPayload,
  CreateOrderResponse,
} from '../../services/order-creation.service';
import { LocationService } from '../../services/location.service';
import { EmployeeService } from '../../services/employee.service';
import { RoleService } from '../../services/role.service';
import { AuthService } from '../../services/auth.service';
import { AmplitudeService } from '../../services/amplitude.service';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { LoggerService } from '../../services/logger.service';
import { OrdersService } from '../../orders/services/orders.service';
import { Order } from '../../orders/models/order.models';
import { Package } from '../../shared/types';
import { UserRole } from '../../shared/types/role.interface';
import { getUserDataLaneId } from '../../shared/utils/auth-headers.util';
import { getIsMobileOrTablet } from '../../shared/utils/device.util';

type OrderStep = 'select-car' | 'customer-info' | 'vehicle-details' | 'payment';

/**
 * Add Order Modal — Thin Orchestrator.
 *
 * Coordinates the 4-step order creation workflow by delegating UI and logic
 * to dedicated step components:
 *   1. PackageStepComponent     – package selection
 *   2. CustomerInfoStepComponent – customer search & info
 *   3. VehicleStepComponent     – vehicle management
 *   4. PaymentStepComponent     – payment processing
 *
 * State shared between steps lives in AddOrderStateService (provided here).
 * Heavy Tap-to-Pay and Pusher logic lives in TapToPayService.
 *
 * Original component: ~7,069 lines.
 * Refactored orchestrator: ~400 lines.
 */
@Component({
  selector: 'app-add-order-modal',
  standalone: true,
  imports: [
    CommonModule,
    OrderStepperComponent,
    PackageStepComponent,
    CustomerInfoStepComponent,
    VehicleStepComponent,
    PaymentStepComponent,
    SuccessModalComponent,
    OrderDetailsModalComponent,
    DiscardChangesModalComponent,
  ],
  templateUrl: './add-order-modal.component.html',
  styleUrls: ['./add-order-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [AddOrderStateService, TapToPayService],
})
export class AddOrderModalComponent implements OnInit, OnChanges, OnDestroy {
  // ── Inputs & Outputs ───────────────────────────────────────────────────────
  @Input() isOpen = false;
  @Input() selectedPackage: Package | null = null;
  @Input() modalTitle = 'Create Order';
  @Input() firstTabLabel = 'Select Car Wash';
  @Input() secondTabLabel = 'Booking Details';

  @Output() close = new EventEmitter<void>();
  @Output() continue = new EventEmitter<{ tierId: string; optionId: string }>();
  @Output() orderCreated = new EventEmitter<void>();
  @Output() viewOrderDetails = new EventEmitter<string>();

  @ViewChild(OrderDetailsModalComponent) orderDetailsModal?: OrderDetailsModalComponent;
  @ViewChild(PaymentStepComponent) paymentStepRef?: PaymentStepComponent;

  // ── State ──────────────────────────────────────────────────────────────────
  private readonly destroy$ = new Subject<void>();
  private modalOpenTime = 0;

  activeStep: OrderStep = 'select-car';
  visitedSteps = new Set<string>(['select-car']);

  // Order creation
  isCreatingOrder = false;
  orderCreationError = '';
  private orderCreateStartTime = 0;
  /** True while admin location list is loading for booking step */
  isLoadingLocations = false;

  // UI overlays
  showCloseConfirmationModal = false;
  showPaymentSuccess = false;
  showOrderDetailsModal = false;
  isLoadingOrderDetails = false;
  selectedOrder: Order | null = null;
  private openingOrderDetailsFromSuccess = false;

  // Computed data for PaymentStep
  priceBreakdown: PriceBreakdown = { subtotal: 0, tax: 0, total: 0, label: '' };
  paymentSummary: PaymentSummary = {
    packageName: '',
    customerName: '',
    vehicleYear: '',
    vehicleMake: '',
    vehicleModel: '',
  };

  successModalData: SuccessModalData = {
    ...PAYMENT_SUCCESS_MODAL_DEFAULTS,
    buttonText: 'Go to Order Details',
  };

  // iOS touch fix
  private readonly isIOSDevice =
    typeof navigator !== 'undefined' &&
    (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.userAgent.includes('Mac') &&
        typeof window !== 'undefined' &&
        'ontouchend' in window));
  private readonly iosInteractiveSelector =
    'button:not([disabled]), [role="button"], .tab-item, .tab-arrow, .continue, .cancel, .customer-search-item, .dropdown-option, .add-vehicle-link, .vehicle-item-wrapper, .payment-method, .show-summary-text, .save-button, .cancel-link, .action-button, .close-btn';

  /**
   * Initializes the add order modal component with all required services for order creation, payment processing, authentication, logging, and UI messaging.
   * @example
   * AddOrderModalComponent(state, tapToPayService, orderCreationService, locationService, employeeService, ordersService, roleService, authService, amplitudeService, messageService, logger)
   * void
   * @param {AddOrderStateService} state - Shared state service for managing add order modal state.
   * @param {TapToPayService} tapToPayService - Service for handling Tap to Pay payment interactions.
   * @param {OrderCreationService} orderCreationService - Service for creating new orders.
   * @param {LocationService} locationService - Service for retrieving and managing location data.
   * @param {EmployeeService} employeeService - Service for retrieving and managing employee data.
   * @param {OrdersService} ordersService - Service for accessing and managing orders.
   * @param {RoleService} roleService - Service for handling role-related logic and permissions.
   * @param {AuthService} authService - Service for authentication and current user access.
   * @param {AmplitudeService} amplitudeService - Service for tracking analytics events.
   * @param {NotificationService} messageService - Service for displaying user-facing messages.
   * @param {LoggerService} logger - Service for logging errors and application events.
   * @returns {void} Does not return a value.
   **/
  constructor(
    public readonly state: AddOrderStateService,
    private readonly tapToPayService: TapToPayService,
    private readonly orderCreationService: OrderCreationService,
    private readonly locationService: LocationService,
    private readonly employeeService: EmployeeService,
    private readonly ordersService: OrdersService,
    private readonly roleService: RoleService,
    private readonly authService: AuthService,
    private readonly amplitudeService: AmplitudeService,
    private readonly messageService: NotificationService,
    private readonly logger: LoggerService
  ) {}

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    const role = this.roleService.getCurrentUserRole();
    if (role === UserRole.ATTENDANT || role === UserRole.ATTENDANT_PLUS) this.loadEmployeeProfile();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue && !changes['isOpen']?.previousValue) {
      this.onModalOpen();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Step tabs ──────────────────────────────────────────────────────────────

  get stepTabs(): StepTab[] {
    return [
      { id: 'select-car', label: this.firstTabLabel },
      { id: 'customer-info', label: this.secondTabLabel },
      { id: 'vehicle-details', label: 'Vehicle Details' },
      { id: 'payment', label: 'Payment' },
    ];
  }

  get sectionTitle(): string {
    switch (this.activeStep) {
      case 'select-car':
        return this.firstTabLabel;
      case 'customer-info':
        return this.secondTabLabel === 'Customer Info' ? 'Customer Information' : 'Booking Details';
      case 'vehicle-details':
        return 'Vehicle Details';
      case 'payment':
        return 'Payment Details';
    }
  }

  get isMobileOrTablet(): boolean {
    return getIsMobileOrTablet();
  }

  // ── Step event handlers ────────────────────────────────────────────────────

  onPackageSelected(selection: PackageSelection): void {
    this.state.selectedTierId = selection.tierId;
    this.state.selectedOptionId = selection.optionId;
    this.state.hasSwitchedToFamilyPlan = false;
    this.updatePriceBreakdown();
  }

  onPackageContinue(selection: PackageSelection): void {
    this.onPackageSelected(selection);
    this.continue.emit({ tierId: selection.tierId, optionId: selection.optionId });
    this.navigateTo('customer-info');
  }

  onCustomerResolved(event: CustomerResolvedEvent): void {
    this.state.customerId = event.customerId;
    this.state.customerForm = { ...event.customerForm };
    this.state.selectedLocationId = event.locationId;
    this.state.selectedLocationName = event.locationName;
    this.state.selectedExistingCustomer = event.existingCustomer;
    this.navigateTo('vehicle-details');
  }

  onVehiclesReady(result: VehicleStepResult): void {
    this.state.familyVehicleSlots = result.familyVehicleSlots;
    this.state.vehicleFormSlots = result.vehicleFormSlots;
    this.state.hasExistingVehicles = result.hasExistingVehicles;
    this.state.hasSwitchedToFamilyPlan = result.hasSwitchedToFamilyPlan;
    this.updatePaymentSummary();
    this.updatePriceBreakdown();
    this.navigateTo('payment');
    this.createOrder();
  }

  onPaymentSuccess(): void {
    this.buildSuccessModalData();
    this.showPaymentSuccess = true;
    this.ordersService.refreshOrders();
  }

  onPaymentFailed(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Payment Failed',
      detail: message || 'Payment could not be processed.',
      life: 5000,
    });
  }

  onCustomerFetched(customer: any): void {
    if (!customer) return;

    // Update state with fetched customer data (Step 3 auto-populates Step 2 data)
    this.state.customerId = String(customer.id || customer.customer_id || '');
    this.state.customerForm.firstName = customer.first_name || customer.firstName || '';
    this.state.customerForm.lastName = customer.last_name || customer.lastName || '';
    this.state.customerForm.phone = customer.contact || customer.phone || '';
    this.state.customerForm.email = customer.email || '';
    this.state.selectedExistingCustomer = customer;

    this.messageService.add({
      severity: 'info',
      summary: 'Customer Found',
      detail: `Linked to existing customer: ${this.state.customerForm.firstName}`,
    });
  }

  onBack(): void {
    const steps: OrderStep[] = ['select-car', 'customer-info', 'vehicle-details', 'payment'];
    const idx = steps.indexOf(this.activeStep);
    if (idx > 0) this.navigateTo(steps[idx - 1]);
  }

  // ── Modal open/close ───────────────────────────────────────────────────────

  onClose(): void {
    if (this.hasFormData()) {
      this.showCloseConfirmationModal = true;
    } else {
      this.closeModal();
    }
  }

  onOverlayClick(event: Event): void {
    if (event.target === event.currentTarget) this.onClose();
  }

  onCloseConfirmationConfirm(): void {
    this.showCloseConfirmationModal = false;
    this.closeModal();
  }

  onCloseConfirmationCancel(): void {
    this.showCloseConfirmationModal = false;
  }

  // ── Success modal ──────────────────────────────────────────────────────────

  onSuccessModalButtonClick(): void {
    this.openingOrderDetailsFromSuccess = true;
    this.showPaymentSuccess = false;
    if (this.state.createdOrderId) {
      this.fetchOrderDetails(this.state.createdOrderId);
    } else {
      this.closeModal();
    }
  }

  onSuccessModalClose(): void {
    if (!this.openingOrderDetailsFromSuccess) this.closeModal();
  }

  /** Called by parent components (e.g. admin-orders) to close the payment link modal inside the payment step. */
  closePaymentLinkModal(): void {
    if (this.paymentStepRef) {
      this.paymentStepRef.showPaymentLinkSentModal = false;
    }
  }

  /**
   * Called by parent (admin/staff-orders) via @ViewChild when a payment link is paid via Pusher.
   * Closes the "link sent" overlay and shows the success modal instead of a bare toast.
   */
  notifyPaymentLinkPaid(): void {
    this.closePaymentLinkModal();
    this.onPaymentSuccess();
  }

  // ── Order details modal ────────────────────────────────────────────────────

  onOrderDetailsClose(): void {
    this.showOrderDetailsModal = false;
    this.selectedOrder = null;
    this.closeModal();
  }

  onOrderCancelled(_order: Order): void {
    this.showOrderDetailsModal = false;
    this.selectedOrder = null;
    this.closeModal();
    this.messageService.add({
      severity: 'success',
      summary: 'Order Canceled',
      detail: 'Order has been canceled successfully.',
    });
  }

  // ── iOS fix ────────────────────────────────────────────────────────────────

  /**
   * Handles iOS touchend events to trigger clicks on eligible interactive elements within the open modal.
   * @example
   * handleIOSTap(event)
   * undefined
   * @param {TouchEvent} event - The touch event fired on the document.
   * @returns {void} No return value.
   **/
  @HostListener('touchend', ['$event'])
  handleIOSTap(event: TouchEvent): void {
    if (!this.isIOSDevice || !this.isOpen) return;
    const target = event.target as HTMLElement | null;
    if (!target) return;
    const el = target.closest(this.iosInteractiveSelector) as HTMLElement | null;
    if (!el) return;
    if (el instanceof HTMLButtonElement && el.disabled) return;
    if (el.hasAttribute('data-ios-tap-handled')) return;
    el.setAttribute('data-ios-tap-handled', 'true');
    event.preventDefault();
    el.click();
    setTimeout(() => el.removeAttribute('data-ios-tap-handled'), 0);
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  /**
   * Handles modal open initialization for the Add Order modal, including state reset, analytics tracking, and deferred data loading.
   * @example
   * onModalOpen()
   * void
   * @returns {void} Initializes modal state and triggers deferred loading/setup actions.
   **/
  private onModalOpen(): void {
    this.modalOpenTime = Date.now();
    this.logger.trackOrder('Add Order modal opened', {
      locationId: this.state.selectedLocationId,
      userRole: this.roleService.getCurrentUserRole(),
    });
    this.state.reset(this.getDefaultPaymentMethod());
    this.state.washTiers = [];
    this.activeStep = 'select-car';
    this.visitedSteps = new Set(['select-car']);
    this.showPaymentSuccess = false;
    this.showCloseConfirmationModal = false;
    this.isCreatingOrder = false;
    this.orderCreationError = '';

    setTimeout(() => {
      if (!this.isOpen) return;
      this.loadLocations();
      if (this.roleService.isShopFloorEmployee()) this.loadEmployeeProfile();
      const user = this.authService.user();
      if (user?.id) {
        this.tapToPayService.initUserPusher(String(user.id));
      }
    }, 0);
  }

  /**
   * Navigates to the specified order step, updates visited state, and tracks relevant analytics events.
   * @example
   * navigateTo('payment')
   * void
   * @param {OrderStep} step - The order step to activate.
   * @returns {void} No return value.
   **/
  private navigateTo(step: OrderStep): void {
    this.activeStep = step;
    this.visitedSteps.add(step);

    if (step === 'vehicle-details') {
      const user = this.authService.user();
      const role = this.roleService.getCurrentUserRole();
      const userType =
        role === 'admin'
          ? 'admin'
          : role === 'attendant' || role === 'attendant_plus' || role === 'staff'
            ? 'attendant'
            : 'customer';
      this.amplitudeService.trackOrderCreated(
        userType,
        this.state.selectedOptionId,
        this.state.selectedLocationId,
        user?.email ?? ''
      );
    }

    this.logger.trackOrder('Tab navigated', {
      tab: step,
      paymentMethod: step === 'payment' ? this.state.paymentForm.method : undefined,
      locationId: this.state.selectedLocationId,
      elapsedSinceOpenMs: this.modalOpenTime ? Date.now() - this.modalOpenTime : undefined,
    });
  }

  /**
   * Creates an order by validating the payload, submitting it to the order service, and handling success or error states.
   * @example
   * createOrder()
   * void
   * @returns {void} Does not return a value.
   **/
  private createOrder(): void {
    if (this.isCreatingOrder) return;
    this.isCreatingOrder = true;
    this.orderCreateStartTime = Date.now();
    this.orderCreationError = '';

    this.logger.trackOrder('Order creation started', {
      customerId: this.state.customerId,
      locationId: this.state.selectedLocationId,
      paymentMethod: this.state.paymentForm.method,
      elapsedSinceOpenMs: this.modalOpenTime ? Date.now() - this.modalOpenTime : undefined,
    });

    try {
      const payload = this.buildOrderPayload();
      const validation = this.orderCreationService.validateOrderPayload(payload);
      if (!validation.isValid) {
        this.orderCreationError = validation.errors.join(', ');
        this.isCreatingOrder = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Order Error',
          detail: this.orderCreationError,
        });
        return;
      }

      this.orderCreationService
        .createOrder(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: CreateOrderResponse) => {
            this.isCreatingOrder = false;
            if (res.status && res.data) {
              this.logger.trackOrder('Order created successfully', {
                orderId: res.data.order.id,
                paymentMethod: this.state.paymentForm.method,
                locationId: this.state.selectedLocationId,
                elapsedMs: Date.now() - this.orderCreateStartTime,
              });
              this.state.createdOrderId = res.data.order.id;
              this.state.connectedStripeAccountId = (res.data.order?.stripe_account_id ?? '')
                .toString()
                .trim();
              this.state.createdOrderResponse = res.data;
              this.orderCreated.emit();
              this.messageService.add({
                severity: 'success',
                summary: 'Order Created',
                detail: 'Order created successfully!',
              });
            } else {
              this.orderCreationError = res.message || 'Failed to create order';
              if (this.orderCreationError.includes('already has an order created today')) {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Order Error',
                  detail: this.orderCreationError,
                });
                this.closeModalAfterDelay(1500);
                return;
              }
              this.messageService.add({
                severity: 'error',
                summary: 'Order Error',
                detail: this.orderCreationError,
              });
            }
          },
          error: err => {
            this.isCreatingOrder = false;
            this.logger.trackError('AddOrderModal.createOrder', err);
            this.orderCreationError = this.extractErrorMessage(
              err,
              'Failed to create order. Please try again.'
            );
            if (this.orderCreationError.includes('already has an order created today')) {
              this.messageService.add({
                severity: 'error',
                summary: 'Order Error',
                detail: this.orderCreationError,
              });
              this.closeModalAfterDelay(1500);
              return;
            }
            this.messageService.add({
              severity: 'error',
              summary: 'Order Error',
              detail: this.orderCreationError,
            });
          },
        });
    } catch (err: any) {
      this.isCreatingOrder = false;
      this.orderCreationError = err?.message ?? 'Failed to prepare order.';
      this.messageService.add({
        severity: 'error',
        summary: 'Order Error',
        detail: this.orderCreationError,
      });
    }
  }

  /**
   * Builds the order payload from the current modal state and selected package information.
   * @example
   * buildOrderPayload()
   * { customer_id: '123', package_id: 'pkg-1', location_id: 'loc-1', vehicle_id: 'veh-1', wash_type: 'single', voucher_code: '', appointment_time: '2024-01-01T00:00:00.000Z', source: 'web', order_source: 'saas' }
   * @returns {CreateOrderPayload} The complete payload required to create an order.
   **/
  private buildOrderPayload(): CreateOrderPayload {
    const option = this.state.selectedPackageOption;
    if (!option) throw new Error('No package selected');
    if (!this.state.customerId) throw new Error('Customer ID is required');

    const locationId = this.resolveLocationId();
    if (!locationId) throw new Error('Location is required — please select a location');

    const vehicleId = this.resolveVehicleId();
    if (!vehicleId) throw new Error('Vehicle ID is required — please select or add a vehicle');

    const packageId = option.id.split('-').slice(0, -1).join('-');
    const washType = this.orderCreationService.getWashType(option.type || 'single');

    const laneId = getUserDataLaneId();
    return {
      customer_id: this.state.customerId,
      package_id: packageId,
      location_id: locationId,
      vehicle_id: vehicleId,
      wash_type: washType,
      voucher_code: '',
      appointment_time: new Date().toISOString(),
      source: 'web',
      order_source: 'saas',
      ...(laneId ? { lane_id: laneId } : {}),
    };
  }

  private resolveLocationId(): string {
    const role = this.roleService.getCurrentUserRole();
    if (role === UserRole.ATTENDANT || role === UserRole.ATTENDANT_PLUS) {
      return this.state.selectedLocationId || (this.employeeProfile?.location_id ?? '');
    }
    return this.state.selectedLocationId;
  }

  private resolveVehicleId(): string {
    const fromFamily = this.state.familyVehicleSlots.find(s => s.bookForWash && s.vehicleData);
    if (fromFamily?.vehicleData) {
      return ((fromFamily.vehicleData as any).id ?? fromFamily.id ?? '').toString();
    }
    const fromForm = this.state.vehicleFormSlots.find(
      s => s.bookForWash && s.vehicleData && s.formData?.vehicleId
    );
    return fromForm?.formData.vehicleId ?? '';
  }

  /**
   * Loads available locations from the location service and updates component state.
   * @example
   * loadLocations()
   * void
   * @returns {void} No return value.
   **/
  private loadLocations(): void {
    this.isLoadingLocations = true;
    this.locationService
      .getLocations()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.isLoadingLocations = false;
          const data = this.extractLocationsArray(res);
          this.state.availableLocations = data;
        },
        error: err => {
          this.isLoadingLocations = false;
          this.state.availableLocations = [];
          this.logger.trackError('AddOrderModal.loadLocations', err);
        },
      });
  }

  /** API may return { data: [] } or nested shapes; locations use `branch` not `name`. */
  private extractLocationsArray(res: any): any[] {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.data?.locations)) return res.data.locations;
    if (Array.isArray(res?.data?.data)) return res.data.data;
    return [];
  }

  /**
   * Loads the current employee profile and synchronizes the employee's location with the order form state.
   * @example
   * loadEmployeeProfile()
   * void
   * @returns {void} No return value.
   **/
  private loadEmployeeProfile(): void {
    this.employeeService
      .getCurrentEmployeeProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (!res.status || !res.data) return;
          this.employeeProfile = res.data;
          if (res.data.location_id) {
            this.state.selectedLocationId = res.data.location_id;
            const loc = this.state.availableLocations.find(
              (l: any) => String(l?.id ?? '') === String(res.data.location_id)
            );
            const label = (loc as any)?.branch ?? (loc as any)?.name ?? 'Staff Location';
            this.state.selectedLocationName = label;
            this.state.customerForm.location = label;
          }
        },
        error: err => this.logger.trackError('AddOrderModal.loadEmployeeProfile', err),
      });
  }

  private employeeProfile: any = null;

  /**
   * Returns a filtered and normalized list of available location options from component state.
   * @example
   * locationOptions()
   * [{ id: '123', name: 'Main Branch' }]
   * @returns {LocationOption[]} Normalized location options with id and display name.
   **/
  get locationOptions(): LocationOption[] {
    return (this.state.availableLocations ?? [])
      .filter((l: any) => l?.status !== false)
      .map((l: any) => {
        const id = String(l?.id ?? l?.location_id ?? '').trim();
        const branch = (l?.branch ?? '').trim();
        const fallback =
          (l?.name ?? l?.shop_name ?? l?.location_name ?? '').trim() ||
          [l?.city, l?.state].filter(Boolean).join(', ').trim();
        const name = branch || fallback || 'Location';
        return id ? { id, name } : null;
      })
      .filter((o): o is LocationOption => o !== null);
  }

  /**
   * Fetches order details by order ID and updates the modal state accordingly.
   * @example
   * fetchOrderDetails('12345')
   * void
   * @param {string} orderId - The unique identifier of the order to retrieve.
   * @returns {void} Does not return a value.
   */
  private fetchOrderDetails(orderId: string): void {
    this.isLoadingOrderDetails = true;
    this.ordersService
      .getOrderById(orderId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (order: Order) => {
          this.isLoadingOrderDetails = false;
          this.selectedOrder = order;
          this.showOrderDetailsModal = true;
        },
        error: err => {
          this.isLoadingOrderDetails = false;
          this.logger.trackError('AddOrderModal.fetchOrderDetails', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to fetch order details.',
          });
          this.closeModal();
        },
      });
  }

  /**
   * Closes the add order modal, tracks analytics, emits the close event, and resets modal state.
   * @example
   * closeModal()
   * undefined
   * @returns {void} No return value.
   **/
  private closeModal(): void {
    this.logger.trackOrder('Add Order modal closed', {
      openDurationMs: this.modalOpenTime ? Date.now() - this.modalOpenTime : undefined,
      lastTab: this.activeStep,
      orderId: this.state.createdOrderId || undefined,
    });
    this.close.emit();
    this.openingOrderDetailsFromSuccess = false;
    setTimeout(() => {
      this.state.reset(this.getDefaultPaymentMethod());
      this.activeStep = 'select-car';
      this.visitedSteps = new Set(['select-car']);
      this.showPaymentSuccess = false;
      this.showOrderDetailsModal = false;
      this.selectedOrder = null;
      this.isCreatingOrder = false;
      this.orderCreationError = '';
    }, 0);
  }

  private closeModalAfterDelay(ms: number): void {
    setTimeout(() => this.closeModal(), ms);
  }

  private hasFormData(): boolean {
    return !!(
      this.state.selectedTierId ||
      this.state.customerForm.firstName ||
      this.state.customerForm.phone ||
      this.state.familyVehicleSlots.length > 0 ||
      this.state.vehicleFormSlots.length > 0 ||
      this.activeStep !== 'select-car'
    );
  }

  private getDefaultPaymentMethod(): PaymentMethod {
    if (this.state.isMonthlyMembership && !this.isMobileOrTablet) return PaymentMethodCode.Card;
    return this.isMobileOrTablet ? PaymentMethodCode.TapToPay : PaymentMethodCode.Cash;
  }

  private updatePriceBreakdown(): void {
    const option = this.state.selectedPackageOption;
    if (!option) {
      this.priceBreakdown = { subtotal: 0, tax: 0, total: 0, label: '' };
      return;
    }
    const subtotal = parseFloat(String(option.price ?? 0)) || 0;
    this.priceBreakdown = {
      subtotal,
      tax: 0,
      total: subtotal,
      label: option.type === 'monthly' ? 'Monthly' : 'Single Wash',
    };
  }

  private updatePaymentSummary(): void {
    const v = this.state.selectedVehicle;
    const opt = this.state.selectedPackageOption;
    this.paymentSummary = {
      packageName: opt ? `${opt.type === 'monthly' ? 'Monthly' : 'Single'} Wash` : '',
      customerName:
        `${this.state.customerForm.firstName || ''} ${this.state.customerForm.lastName || ''}`.trim() ||
        'Customer',
      vehicleYear: v?.year ?? '',
      vehicleMake: v?.make ?? '',
      vehicleModel: v?.model ?? '',
    };
  }

  /**
   * Builds the data object used to display the payment success modal.
   * @example
   * buildSuccessModalData()
   * void
   * @returns {void} No return value.
   */
  private buildSuccessModalData(): void {
    const v = this.state.selectedVehicle;
    this.successModalData = buildPaymentSuccessModalData(
      {
        packageName: this.paymentSummary.packageName,
        customerName: this.paymentSummary.customerName,
        vehicleYear: String(v?.year ?? ''),
        vehicleMake: v?.make ?? '',
        vehicleModel: v?.model ?? '',
      },
      { buttonText: 'Go to Order Details' }
    );
  }

  private extractErrorMessage(err: any, fallback: string): string {
    if (err?.status === 500) return 'Server error occurred. Please try again later.';
    if (err?.status === 401) return 'Authentication failed. Please login again.';
    if (err?.status === 400) return err?.error?.message ?? 'Invalid request data.';
    return err?.error?.message ?? err?.message ?? fallback;
  }
}
